import { combine, createEffect, createEvent, createStore } from 'effector';
import {
  $session,
  fetchRecordflowToken,
  logoutRequesting,
} from '@features/common';

import {
  rfFetcher,
  exchangeSsoToken,
  fetchMe,
  fetchWorkspaces,
  createWorkspace,
} from '../api';
import { createRequestState } from '../lib/request-state';

/**
 * RecordFlow is signed into with the OCA account: the OCA backend issues a
 * one-time SSO token and RecordFlow exchanges it for its own JWT. The JWT is
 * stored together with the OCA user it was issued for, so a different OCA
 * user on the same browser never reuses it.
 */
const SESSION_KEY = `${process.env.REACT_APP_PREFIX}:recordflow-session`;
const WORKSPACE_KEY = `${process.env.REACT_APP_PREFIX}:recordflow-workspace`;

function ocaUserKey() {
  const user = $session.getState();
  if (!user) return null;
  return String(user.id || user.email || user.username || '');
}

function readStoredToken() {
  try {
    const stored = JSON.parse(localStorage.getItem(SESSION_KEY));
    return stored && stored.owner === ocaUserKey() ? stored.token : null;
  } catch (e) {
    return null;
  }
}

// Events

export const tokenChanged = createEvent('recordflow token changed');
export const sessionCleared = createEvent('recordflow session cleared');
export const workspaceSwitched = createEvent('recordflow workspace switched');
/** Fires with the new workspace id whenever the current workspace changes. */
export const workspaceChanged = createEvent('recordflow workspace changed');
export const userChanged = createEvent('recordflow user changed');

// Effects

export const startSessionFx = createEffect('start recordflow session');
export const refreshWorkspacesFx = createEffect(
  'refresh recordflow workspaces',
);
export const createWorkspaceFx = createEffect('create recordflow workspace');
export const refreshUserFx = createEffect('refresh recordflow user');

// Stores

export const $rfToken = createStore(null)
  .on(tokenChanged, (_, token) => token)
  .reset(sessionCleared);

export const $rfUser = createStore(null)
  .on(startSessionFx.done, (_, { result }) => result.user)
  .on(refreshUserFx.done, (_, { result }) => result)
  .on(userChanged, (_, user) => user)
  .reset(sessionCleared);

export const $workspaces = createStore([])
  .on(startSessionFx.done, (_, { result }) => result.workspaces)
  .on(refreshWorkspacesFx.done, (_, { result }) => result)
  .reset(sessionCleared);

export const $currentWorkspaceId = createStore(
  localStorage.getItem(WORKSPACE_KEY),
)
  .on(workspaceSwitched, (_, id) => id)
  // Keep the chosen workspace while it still exists, otherwise the first one.
  .on($workspaces.updates, (current, rows) => {
    if (current && rows.some(row => row.id === current)) return current;
    return rows.length ? rows[0].id : null;
  })
  .reset(sessionCleared);

export const $currentWorkspace = combine(
  $workspaces,
  $currentWorkspaceId,
  (rows, id) => rows.find(row => row.id === id) || null,
);

export const $sessionRequest = createRequestState(startSessionFx, {
  reset: sessionCleared,
});
export const $isSessionReady = $rfUser.map(user => user !== null);

// Side effects

$rfToken.updates.watch(token => {
  if (token) {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ token, owner: ocaUserKey() }),
    );
  } else {
    localStorage.removeItem(SESSION_KEY);
  }
});

$currentWorkspaceId.updates.watch(id => {
  if (id) localStorage.setItem(WORKSPACE_KEY, id);
  else localStorage.removeItem(WORKSPACE_KEY);
  workspaceChanged(id);
});

logoutRequesting.done.watch(() => sessionCleared());

let renewal = null;

/**
 * Signs in to RecordFlow with a fresh OCA SSO token. Concurrent callers share
 * one request, so a burst of expired requests renews the session once.
 */
export function renewSession() {
  if (!renewal) {
    renewal = fetchRecordflowToken()
      .then(({ data }) => {
        if (!data || !data.token) {
          throw new Error('OCA did not return a RecordFlow token');
        }
        return exchangeSsoToken(data.token);
      })
      .then(response => {
        tokenChanged(response.access_token);
        return response.access_token;
      })
      .finally(() => {
        renewal = null;
      });
  }
  return renewal;
}

rfFetcher.interceptors.request.use(config => {
  const token = $rfToken.getState();
  if (token && !config.headers.Authorization) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// An expired RecordFlow token is renewed through SSO and the request retried once.
rfFetcher.interceptors.response.use(undefined, async error => {
  const config = error && error.config;
  const isSsoCall = config && /\/auth\/sso$/.test(config.url || '');
  if (!config || error.status !== 401 || config.retried || isSsoCall) {
    throw error;
  }
  const token = await renewSession();
  return rfFetcher({
    ...config,
    retried: true,
    headers: { ...config.headers, Authorization: `Bearer ${token}` },
  });
});

// Thunks

startSessionFx.use(async () => {
  if (!$rfToken.getState()) {
    const stored = readStoredToken();
    if (stored) tokenChanged(stored);
    else await renewSession();
  }
  const [user, workspaces] = await Promise.all([fetchMe(), fetchWorkspaces()]);
  return { user, workspaces };
});

refreshWorkspacesFx.use(fetchWorkspaces);
refreshUserFx.use(fetchMe);

createWorkspaceFx.use(async name => {
  const workspace = await createWorkspace({ name });
  await refreshWorkspacesFx();
  workspaceSwitched(workspace.id);
  return workspace;
});
